import React, { useState, useEffect } from 'react';
import { func, string } from 'prop-types';
import {
  TextFieldLabel, Select, Button, Spinner, DatePicker, Checkbox,
} from 'ui-component-library';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import moment from 'moment';
import theme from '../../../../../../theme';

import {
  FormHeader,
  TopRowContainer,
  StatusInputContainer,
  DriverLocationInputContainer,
  LateReasonInputContainer,
  InputDateContainer,
  InputTimeContainer,
  SubmitButtonContainer,
  LoadingContainer,
  AdditionalFieldsContainer,
  SubText,
  EquipmentLabel,
  EquipmentLabelValue,
  EquipmentContainer,
  AcknowledgementContainer,
} from './action-update-status.style';
import { updateLoadStatus } from '../../../../../../utils/api/load';
import {
  showSuccessMessage, showErrorMessage, logger, parseDate,
} from '../../../../../../utils/common';
import loadShape from '../../../../../../shapes/load';
import CommentBox from '../../../comments-box';
import { ACTIVITY_TYPES, COMMENTS_CHARACTER_LIMIT, MODE_TYPES } from '../../../../../../constants';
import {
  getLocationOptionsFromShipmentInformation, getNextLocationOption,
  getPreviousLocationOption, formatErrorResponse, createOption,
  getLtlLocationOption,
} from '../../../../../../utils/mappers';
import TimeInput from '../../../time-input';

// Validation Schema
export const tlSchema = yup.object().shape({
  newTLStatus: yup.string().required(),
  oldTLStatus: yup.string().required(),
  driverCity: yup.string().when('newTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusAtConsignee || val === ACTIVITY_TYPES.updateStatusAtShipper
      || val === ACTIVITY_TYPES.updateStatusInTransit || val === ACTIVITY_TYPES.updateStatusPaperwork),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  driverState: yup.string().when('newTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusAtShipper || val === ACTIVITY_TYPES.updateStatusAtConsignee
      || val === ACTIVITY_TYPES.updateStatusInTransit || val === ACTIVITY_TYPES.updateStatusPaperwork),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  note: yup.string().optional()
    .nullable()
    .max(COMMENTS_CHARACTER_LIMIT, `Comment cannot be less that ${COMMENTS_CHARACTER_LIMIT}`),
  checkInTime: yup.string().when('newTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusAtShipper || val === ACTIVITY_TYPES.updateStatusAtConsignee),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  checkOutTime: yup.string().when('newTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusInTransit || val === ACTIVITY_TYPES.updateStatusPaperwork),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  lateReason: yup.string().when('$requiresLateReason', {
    is: true,
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
});

// Validation Schema
export const ltlSchema = yup.object().shape({
  newLTLStatus: yup.string().required(),
  oldLTLStatus: yup.string().required(),
  driverCity: yup.string().when('newLTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusPickup || val === ACTIVITY_TYPES.updateStatusDelivered),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  driverState: yup.string().when('newLTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusPickup || val === ACTIVITY_TYPES.updateStatusDelivered),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  note: yup.string().optional()
    .nullable()
    .max(COMMENTS_CHARACTER_LIMIT, `Comment cannot be less that ${COMMENTS_CHARACTER_LIMIT}`),
  checkInTime: yup.string().when('newLTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusPickup),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
  checkOutTime: yup.string().when('newLTLStatus', {
    is: (val) => (val === ACTIVITY_TYPES.updateStatusDelivered),
    then: yup.string().required(),
    otherwise: yup.string().optional().nullable(),
  }),
});

/**
 * Update status action form
 * @param {object} params - Contains props for this component
 * @param {object} load - Load data to display
 * @param {function} params.refetchLoad - Function to reload load summary in parent
 * @param {string} params.mode - Specifies the SalesOrderMode
 * @return {element} - The update status form
 */
const UpdateStatusAction = ({ load, refetchLoad, mode }) => {
  const { shipmentInformation, driverInformation } = load;

  // State hooks
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState('');
  const [date, setDate] = useState(null);
  const [stopLocation, setStopLocation] = useState(null);
  const [lateReason, setLateReason] = useState(null);
  const [equipmentAcknowledged, setEquipmentAcknowledged] = useState(false);

  // Redux hooks
  const metadata = useSelector((state) => state.metadata);

  // Enforce business logic rules on statusUpdateEvents having check in and check out times on certain LoadStatuses:
  // When a user changes status to At Shipper or At Consignee, then they must add a CheckIn date and time in the stop's local time zone.
  // When a user changes status to Paperwork or to InTransit, then they must add a CheckOut date and time in the stop's local time zone.
  const requiresCheckIn = (selectedStatus?.value === ACTIVITY_TYPES.updateStatusAtShipper
    || selectedStatus?.value === ACTIVITY_TYPES.updateStatusAtConsignee)
    || (mode === MODE_TYPES.lessThanTruckload && (selectedStatus?.value === ACTIVITY_TYPES.updateStatusPickup
      || selectedStatus?.val === ACTIVITY_TYPES.updateStatusDelivered));

  const requiresCheckOut = (mode !== MODE_TYPES.lessThanTruckload
    && selectedStatus?.value === ACTIVITY_TYPES.updateStatusInTransit)
    || selectedStatus?.value === ACTIVITY_TYPES.updateStatusPaperwork
    || (mode === MODE_TYPES.lessThanTruckload && selectedStatus?.value === ACTIVITY_TYPES.updateStatusDelivered);

  const isDispatchStatus = selectedStatus?.value === ACTIVITY_TYPES.updateStatusDispatch;

  // Late reason is only shown and required if the check in time is after the appointment time
  const requiresLateReason = mode === MODE_TYPES.truckload && requiresCheckIn
    && stopLocation?.value?.appointmentTime && date && time
    && moment.utc(stopLocation?.value?.appointmentTime).isBefore(moment.utc(parseDate(date, time)));

  /**
   * Function to clear the current state of the form
   * @return {void}
   */
  const resetState = () => {
    setSelectedStatus(null);
    setComments(null);
    setStopLocation(null);
    setTime('');
    setDate(null);
    setLateReason(null);
    setEquipmentAcknowledged(false);
  };

  // Set the pre-fill stop location based on requiresCheckIn, requiresCheckOut
  // and shipmentInformation changing
  useEffect(() => {
    if (mode === MODE_TYPES.truckload) {
      setStopLocation(
        requiresCheckIn ? getNextLocationOption(shipmentInformation) : getPreviousLocationOption(shipmentInformation),
      );
    } else {
      setStopLocation(getLtlLocationOption(shipmentInformation, selectedStatus?.value));
    }
  }, [requiresCheckIn, requiresCheckOut, shipmentInformation, selectedStatus, mode]);

  useEffect(() => {
    resetState();
  }, [load]);

  /**
   * Checks if the form is valid
   * @return {boolean}
   */
  const checkValid = (modeType) => {
    let addressInfo = {};
    if (requiresCheckIn || requiresCheckOut) {
      addressInfo = {
        city: stopLocation?.value.city,
        state: stopLocation?.value.state,
        locationNumber: stopLocation?.value.locationNumber,
      };
    }
    const payload = {
      newTLStatus: modeType === MODE_TYPES.truckload ? selectedStatus?.value : undefined,
      oldTLStatus: modeType === MODE_TYPES.truckload ? load.tlStatus : undefined,
      newLTLStatus: modeType === MODE_TYPES.lessThanTruckload ? selectedStatus?.value : undefined,
      oldLTLStatus: modeType === MODE_TYPES.lessThanTruckload ? load.ltlStatus : undefined,
      note: comments,
      driverCity: addressInfo?.city || '',
      driverState: addressInfo?.state || '',
      checkInTime: requiresCheckIn ? parseDate(date, time) : undefined,
      checkOutTime: requiresCheckOut ? parseDate(date, time) : undefined,
      lateReason: requiresLateReason ? lateReason?.value : undefined,
    };
    if (modeType === MODE_TYPES.truckload) {
      const valid = tlSchema.isValidSync(payload, { context: { requiresLateReason, modeType } });
      if (isDispatchStatus && !equipmentAcknowledged) return false;
      return valid;
    }
    const valid = ltlSchema.isValidSync(payload, { context: { requiresLateReason, modeType } });
    return valid;
  };

  useEffect(() => {
    resetState();
  }, [load]);

  /**
   * Handles when the selected status has changed
   * @param {object} event - Change event
   * @return {void}
   */
  const handleSelectedStatusChanged = (event) => {
    setSelectedStatus(event);
  };

  /**
   * Handles when comments change
   * @param {object} event - Change event
   * @return {void}
   */
  const handleCommentsChanged = (event) => {
    setComments(event.target.value);
  };

  /**
   * Handles when check in/out date changes
   * @param {object} event - Change event
   * @return {void}
   */
  const handleDateChanged = (event) => {
    setDate(event);
    if (!requiresLateReason) {
      setLateReason(null);
    }
  };

  /**
   * Handles when check in/out time changes
   * @param {object} event - Change event
   * @return {void}
   */
  const handleTimeChanged = (event) => {
    setTime(event.target.value);
    if (!requiresLateReason) {
      setLateReason(null);
    }
  };

  /**
   * Handles when the stop location dropdown
   * has changed (at shipper and at consignee updates)
   * @param {object} event - DOM event
   * @return {void}
   */
  const handleStopLocationChanged = (event) => {
    setStopLocation(event);
    setTime('');
    setDate(null);
    setLateReason(null);
  };

  /**
   * Handles when the late reason dropdown
   * has changed (at shipper and at consignee updates)
   * @param {object} event - DOM event
   * @return {void}
   */
  const handleLateReasonChanged = (event) => {
    setLateReason(event);
  };

  /**
   * Handles submitting this form.
   * @return {void}
   */
  const handleSubmit = (modeType) => {
    const valid = checkValid(modeType);
    if (valid) {
      let addressInfo = {};
      if (requiresCheckIn || requiresCheckOut) {
        addressInfo = {
          city: stopLocation?.value.city,
          state: stopLocation?.value.state,
          locationNumber: stopLocation?.value.locationNumber,
          appointmentTime: stopLocation?.value.appointmentTime,
        };
      }
      const payload = {
        newTLStatus: modeType === MODE_TYPES.truckload ? selectedStatus?.value : undefined,
        oldTLStatus: modeType === MODE_TYPES.truckload ? load.tlStatus : undefined,
        newLTLStatus: modeType === MODE_TYPES.lessThanTruckload ? selectedStatus?.value : undefined,
        oldLTLStatus: modeType === MODE_TYPES.lessThanTruckload ? load.ltlStatus : undefined,
        note: comments,
        driverCity: addressInfo.city || '',
        driverState: addressInfo.state || '',
        locationNumber: addressInfo.locationNumber || undefined,
        checkInTime: requiresCheckIn ? parseDate(date, time) : undefined,
        checkOutTime: requiresCheckOut ? parseDate(date, time) : undefined,
        appointmentTime: requiresCheckIn ? addressInfo.appointmentTime : undefined,
        lateReason: requiresLateReason ? lateReason?.value : undefined,
      };

      setLoading(true);
      updateLoadStatus({
        ...payload,
        loadId: load.loadId,
        modeType,
      }, load.billOfLadingNumber).then(() => {
        const newStatus = mode === MODE_TYPES.truckload ? payload.newTLStatus : payload.newLTLStatus;
        showSuccessMessage(`Status updated to ${newStatus} on Load #${load.billOfLadingNumber}. This information may take a moment to update.`);
        resetState();
        refetchLoad(mode === MODE_TYPES.truckload ? { tlStatus: payload.newTLStatus }
          : { ltlStatus: payload.newLTLStatus });
      }).catch((error) => {
        logger.error(formatErrorResponse(error));
        const errorMessage = modeType === MODE_TYPES.truckload ? 'The status change requested for this load is invalid. Make sure all check in and check out information has been entered for all previous stops.'
          : 'The status change requested for this load is not valid. If entering an actual delivery date, ensure it is after the original pickup date of the order.';
        showErrorMessage(errorMessage);
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  /**
   * Renders the driver location input depending on
   * which type of status update this is.
   * @return {element}
   */
  const renderLocationInput = (status) => {
    let label = status === 'Pickup' ? '*Pickup Location' : '*Drop off Location';
    if (mode === MODE_TYPES.truckload) {
      label = requiresCheckIn ? '*Check In Location' : '*Check Out Location';
    }
    return (
      <>
        <TextFieldLabel text={label} />
        <Select
          selectedValue={stopLocation}
          options={getLocationOptionsFromShipmentInformation(load.shipmentInformation, selectedStatus?.value, mode)}
          onValueChange={handleStopLocationChanged}
          placeholder="Select Location"
        />
      </>
    );
  };

  const getLocationText = (status) => {
    if (mode === MODE_TYPES.truckload) {
      return requiresCheckIn ? 'check in' : 'out';
    }
    return status === 'Pickup' ? 'pickup' : 'drop off';
  };

  if (loading) {
    return (
      <div data-testid="Update Status Action">
        <FormHeader>Update Status</FormHeader>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </div>
    );
  }

  const timeText = (status) => {
    if (mode === MODE_TYPES.truckload) {
      if (requiresCheckIn) {
        return 'Check In';
      }
      return 'Check Out';
    }

    return status === 'Pickup' ? 'Pickup' : 'Drop off';
  };

  // Construct status options
  const tlStatusOptions = metadata.data.assignableTLStatuses?.map(createOption) || [];
  const ltlStatusOptions = metadata.data.assignableLTLStatuses?.map(createOption) || [];
  // Remove 'None' as an option
  const lateReasons = metadata.data.lateReasons.filter((reason) => reason !== 'None');
  // Construct late reason options
  const lateReasonOptions = lateReasons?.filter((reason) => reason !== 'None').map(createOption) || [];

  // Check if the form input is valid on each render
  const valid = checkValid(mode);

  return (
    <div data-testid="Update Status Action">
      <FormHeader>Update Status</FormHeader>
      <TopRowContainer>
        <StatusInputContainer>
          <TextFieldLabel text="*Status" />
          <Select
            selectedValue={selectedStatus}
            options={mode === MODE_TYPES.truckload ? tlStatusOptions : ltlStatusOptions}
            placeholder={`Current Status: ${mode === MODE_TYPES.truckload ? load.tlStatus : load.ltlStatus}`}
            onValueChange={handleSelectedStatusChanged}
          />
        </StatusInputContainer>
      </TopRowContainer>
      {(requiresCheckIn || requiresCheckOut) && (
        <>
          <SubText>
            {`Add driver location, ${getLocationText(selectedStatus.value)} date and time in the stop's local time zone.`}
          </SubText>
          <AdditionalFieldsContainer>
            <DriverLocationInputContainer>
              {renderLocationInput(selectedStatus.value)}
            </DriverLocationInputContainer>
            <InputDateContainer>
              <TextFieldLabel text={`*${timeText(selectedStatus.value)} Date`} />
              <DatePicker
                selectedDate={date}
                onValueChange={handleDateChanged}
                placeholder="Choose Date"
              />
            </InputDateContainer>
            <InputTimeContainer>
              <TextFieldLabel text={`*${timeText(selectedStatus.value)} Time`} />
              <TimeInput
                value={time}
                onValueChanged={handleTimeChanged}
              />
            </InputTimeContainer>
            {requiresLateReason && (
              <LateReasonInputContainer>
                <TextFieldLabel text="*Late Reason" />
                <Select
                  selectedValue={lateReason}
                  options={lateReasonOptions}
                  onValueChange={handleLateReasonChanged}
                  placeholder="Select Late Reason"
                />
              </LateReasonInputContainer>
            )}
          </AdditionalFieldsContainer>
        </>
      )}
      <CommentBox
        comment={comments}
        onCommentChanged={handleCommentsChanged}
        placeholder="Include helpful details related to the status update."
        characterLimit={COMMENTS_CHARACTER_LIMIT}
      />
      {isDispatchStatus
        ? (
          <>
            <EquipmentContainer>
              <EquipmentLabel>
                <TextFieldLabel text="Equipment:" />
                <EquipmentLabelValue>{driverInformation.equipment}</EquipmentLabelValue>
              </EquipmentLabel>
              <EquipmentLabel>
                <TextFieldLabel text="Length:" />
                <EquipmentLabelValue>{`${driverInformation.length} ft`}</EquipmentLabelValue>
              </EquipmentLabel>
            </EquipmentContainer>
            <SubmitButtonContainer justifyContent="space-between">
              {isDispatchStatus && (
                <AcknowledgementContainer>
                  <Checkbox
                    text="I have confirmed the Equipment Type and Length is correct"
                    checked={equipmentAcknowledged}
                    onValueChange={() => setEquipmentAcknowledged(!equipmentAcknowledged)}
                    labelColor={theme.palette.black[100]}
                  />
                </AcknowledgementContainer>
              )}
              <Button onClick={() => handleSubmit(mode)} text="Submit" isDisabled={!valid} />
            </SubmitButtonContainer>
          </>
        )
        : (
          <SubmitButtonContainer>
            <Button onClick={() => handleSubmit(mode)} text="Submit" isDisabled={!valid} />
          </SubmitButtonContainer>
        )}
    </div>
  );
};

UpdateStatusAction.propTypes = {
  load: loadShape.isRequired,
  refetchLoad: func,
  mode: string.isRequired,
};

UpdateStatusAction.defaultProps = {
  refetchLoad: () => { },
};

export default UpdateStatusAction;
